import { defineStore } from "pinia";
import _ from "lodash";
import { api } from "@/api/base";
import { useMainStore } from "@/pinia/main";

type CopiedPage = {
  page: any;
  popupsByGroupId: any;
};

export type AssetTag = {
  tagId: string;
  displayName: string;
  normalizedName: string;
  ownerGroupId: string;
  usageCount?: number;
};

export type EditorState = {
  selectedPageId: string | null;
  selectedPopupId: string | null;
  selectedElementId: string | null;
  insertingAtIndex: number | null;
  showElementLibrary: boolean;
  showElementPopupBar: boolean;
  showElementSettings: boolean;
  copiedQuestionnairePage: CopiedPage | null;
  assetValidationFailures: Record<string, any>;
  assetSettingsAssetId: string | null;
  mapRefreshKey: number;
  // Map of groupId -> asset tags for that group
  assetTagsByGroup: Record<string, AssetTag[]>;
  // Map of groupId -> selected tag ID for asset filtering
  selectedAssetTagIdByGroup: Record<string, string>;
};

export const getEditorDefaultState = (): EditorState => {
  return {
    selectedPageId: null,
    selectedPopupId: null,
    selectedElementId: null,
    insertingAtIndex: null,
    showElementLibrary: false,
    showElementPopupBar: false,
    showElementSettings: false,
    copiedQuestionnairePage: null,
    assetValidationFailures: {},
    assetSettingsAssetId: null,
    mapRefreshKey: 0, // for updating map when assets are changed
    assetTagsByGroup: {},
    selectedAssetTagIdByGroup: {},
  };
};

export const useEditorStore = defineStore("editor", {
  // Note: No persistence is added, as the original Vuex module did not have it.
  state: (): EditorState => getEditorDefaultState(),

  getters: {
    hasCopiedQuestionnairePage: (state: EditorState): boolean => {
      return !!state.copiedQuestionnairePage;
    },
    assetTags(): AssetTag[] {
      const mainStore = useMainStore();
      const groupId = mainStore.activeGroupId;
      if (!groupId) return [];
      return this.assetTagsByGroup[groupId] || [];
    },
  },

  actions: {
    // Editor UI state actions (converted from mutations)
    setSelectedPageId(value: string | null) {
      if (this.selectedPageId !== value) {
        // Keep UI consistent
        this.selectedElementId = null;
        this.showElementPopupBar = false;
        this.showElementSettings = false;
      }
      this.selectedPageId = value;
    },

    setSelectedPopupId(value: string | null) {
      this.selectedPopupId = value;
    },

    setSelectedElementId(value: string | null) {
      if (_.isNil(value)) {
        this.showElementSettings = false;
      }
      this.selectedElementId = value;
    },

    setShowElementLibrary(bool: boolean) {
      this.showElementLibrary = bool;
    },

    setShowElementSettings(bool: boolean) {
      this.showElementSettings = bool;
    },

    setShowElementPopupBar(bool: boolean) {
      // Make sure UI is consistent
      if (bool) {
        this.showElementLibrary = false;
        this.showElementSettings = false;
      }
      this.showElementPopupBar = bool;
    },

    resetEditorState() {
      this.selectedPageId = null;
      this.selectedPopupId = null;
      this.selectedElementId = null;
      this.showElementLibrary = false;
      this.showElementPopupBar = false;
      this.showElementSettings = false;
      // Note: This does NOT reset asset copied page
    },

    addAssetValidationFailures(obj: Record<string, any>) {
      this.assetValidationFailures = {
        ...this.assetValidationFailures,
        ...obj,
      };
    },

    copyQuestionnairePage(page: CopiedPage) {
      this.copiedQuestionnairePage = { ...page };
    },

    setAssetSettingsAssetId(assetId: string) {
      this.assetSettingsAssetId = assetId;
    },

    removeAssetSettingsAssetId() {
      this.assetSettingsAssetId = null;
    },

    updateMapRefreshKey() {
      this.mapRefreshKey++;
    },

    setInsertingAtIndex(index: number | null) {
      this.insertingAtIndex = index;
    },

    hasAssetTagsForCurrentGroup(): boolean {
      const mainStore = useMainStore();
      const groupId = mainStore.activeGroupId;
      if (!groupId) return false;
      return groupId in this.assetTagsByGroup;
    },

    async fetchAssetTags() {
      const mainStore = useMainStore();
      if (!mainStore.activeGroupId) return;
      try {
        const response = await api.post("/asset/tag/list", {
          ownerGroupId: mainStore.activeGroupId,
        });
        this.assetTagsByGroup[mainStore.activeGroupId] = response.data.response.tags || [];
      } catch (error) {
        console.error("Failed to fetch asset tags:", error);
      }
    },

    setAssetTags(tags: AssetTag[]) {
      const mainStore = useMainStore();
      if (!mainStore.activeGroupId) return;
      this.assetTagsByGroup[mainStore.activeGroupId] = tags;
    },

    addAssetTag(tag: AssetTag) {
      const mainStore = useMainStore();
      if (!mainStore.activeGroupId) return;
      const currentTags = this.assetTagsByGroup[mainStore.activeGroupId] || [];
      this.assetTagsByGroup[mainStore.activeGroupId] = [...currentTags, tag];
    },

    removeAssetTag(tagId: string) {
      const mainStore = useMainStore();
      if (!mainStore.activeGroupId) return;
      const currentTags = this.assetTagsByGroup[mainStore.activeGroupId] || [];
      this.assetTagsByGroup[mainStore.activeGroupId] = currentTags.filter(
        (t: AssetTag) => t.tagId !== tagId
      );
    },

    incrementTagUsageCount(tagId: string) {
      const mainStore = useMainStore();
      if (!mainStore.activeGroupId) return;
      const currentTags = this.assetTagsByGroup[mainStore.activeGroupId] || [];
      const tag = currentTags.find((t: AssetTag) => t.tagId === tagId);
      if (tag) {
        tag.usageCount = (tag.usageCount || 0) + 1;
      }
    },

    decrementTagUsageCount(tagId: string) {
      const mainStore = useMainStore();
      if (!mainStore.activeGroupId) return;
      const currentTags = this.assetTagsByGroup[mainStore.activeGroupId] || [];
      const tag = currentTags.find((t: AssetTag) => t.tagId === tagId);
      if (tag && tag.usageCount && tag.usageCount > 0) {
        tag.usageCount -= 1;
      }
    },

    getSelectedAssetTagId(groupId: string): string | null {
      return this.selectedAssetTagIdByGroup[groupId] || null;
    },

    setSelectedAssetTagId(groupId: string, tagId: string | null) {
      if (tagId) {
        this.selectedAssetTagIdByGroup[groupId] = tagId;
      } else {
        delete this.selectedAssetTagIdByGroup[groupId];
      }
    },
  },
});
